import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, Search } from 'lucide-react';
import { ProjectLabel } from './ui.jsx';

function projectLastUsed(project, entries) {
  return entries.reduce((latest, entry) => {
    if (entry.projectId !== project.id) return latest;
    const stamp = `${entry.workDate}-${entry.start}`;
    return !latest || stamp > latest ? stamp : latest;
  }, '');
}

function recentProjects(projects, entries, count) {
  return [...projects]
    .sort((a, b) => {
      const lastA = projectLastUsed(a, entries);
      const lastB = projectLastUsed(b, entries);
      if (lastA !== lastB) return lastB.localeCompare(lastA);
      return a.name.localeCompare(b.name);
    })
    .slice(0, count);
}

export function ProjectPicker({
  projects,
  entries = [],
  value,
  onChange,
  mode = 'both',
  recentsCount = 5,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const ref = useRef(null);
  const selected = projects.find((project) => project.id === value);

  useEffect(() => {
    if (!open) return undefined;
    function handleClick(event) {
      if (ref.current && !ref.current.contains(event.target)) {
        setOpen(false);
        setQuery('');
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);
  const recents = useMemo(
    () => recentProjects(projects, entries, recentsCount),
    [projects, entries, recentsCount],
  );
  const filtered = projects.filter((project) =>
    `${project.code} ${project.name} ${project.clientName}`
      .toLowerCase()
      .includes(query.toLowerCase()),
  );
  const grouped = filtered.reduce((acc, project) => {
    const client = project.clientName || 'No client';
    acc[client] ||= [];
    acc[client].push(project);
    return acc;
  }, {});

  function choose(id) {
    onChange(id);
    setOpen(false);
    setQuery('');
  }

  return (
    <div className="project-picker" ref={ref}>
      {mode !== 'picker' && (
        <div className="project-chip-row">
          {recents.map((project) => (
            <button
              type="button"
              key={project.id}
              className={`project-chip ${project.id === value ? 'active' : ''}`}
              onClick={() => choose(project.id)}
              data-tooltip={`${project.code} · ${project.name}`}
            >
              <ProjectLabel project={project} />
            </button>
          ))}
          <button
            type="button"
            className="project-chip more"
            onClick={() => setOpen(!open)}
          >
            More <ChevronDown size={13} />
          </button>
        </div>
      )}
      {mode !== 'chips' && (
        <button
          type="button"
          className="project-picker-trigger"
          onClick={() => setOpen(!open)}
        >
          {selected ? (
            <ProjectLabel project={selected} />
          ) : (
            <span>Select project</span>
          )}
          <ChevronDown size={14} />
        </button>
      )}
      {open && (
        <div className="project-picker-popover">
          <label className="project-picker-search">
            <Search size={14} />
            <input
              autoFocus
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search projects..."
            />
          </label>
          {!query && (
            <div className="project-picker-section">
              <strong>Recent</strong>
              {recents.map((project) => (
                <ProjectOption
                  key={project.id}
                  project={project}
                  onChoose={choose}
                />
              ))}
            </div>
          )}
          <div className="project-picker-section">
            <strong>{query ? 'Results' : 'All projects'}</strong>
            {Object.entries(grouped).map(([clientName, items]) => (
              <div key={clientName} className="project-picker-client">
                {!query && projects.length > 8 && <span>{clientName}</span>}
                {items.map((project) => (
                  <ProjectOption
                    key={project.id}
                    project={project}
                    onChoose={choose}
                  />
                ))}
              </div>
            ))}
            {!filtered.length && <em>No projects found</em>}
          </div>
        </div>
      )}
    </div>
  );
}

function ProjectOption({ project, onChoose }) {
  return (
    <button
      type="button"
      className="project-option"
      onClick={() => onChoose(project.id)}
    >
      <ProjectLabel project={project} />
      <small>{project.clientName}</small>
    </button>
  );
}
